/**
 * Token Manager
 * Secure token storage and lifecycle management
 */

import { TokenPair, TokenStatus } from '../../types/auth';
import { logger } from '../../utils/logger';

export interface ITokenManager {
  // Token storage
  storeTokens(tokens: TokenPair & { isLegacyToken?: boolean }, bypassIntegrityCheck?: boolean): void;
  getTokens(): TokenPair | null;
  clearTokens(): void;
  
  // Multi-account support
  setAccountContext(accountId: string | null): void;
  getAccountContext(): string | null;
  
  // Token validation
  isAccessTokenValid(): boolean;
  isRefreshTokenValid(): boolean;
  getTokenExpiration(): number | null; // Changed to number timestamp
  
  // Automatic refresh. `force` skips the "already valid" short-circuit — required on the 401
  // path, where the SERVER has rejected a token the local clock still considers valid.
  refreshTokensIfNeeded(force?: boolean): Promise<boolean>;
  scheduleTokenRefresh(): void;
  
  // Security
  validateTokenIntegrity(token: string): boolean;
  clearMigrationFlag(): void;
  
  // Lifecycle management
  cleanup(): void;
}

/**
 * Storage keys for token management
 * Note: These are base keys. For multi-account support, we append account ID
 */
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'smartmatrix_access_token',
  REFRESH_TOKEN: 'smartmatrix_refresh_token',
  TOKEN_EXPIRES_AT: 'smartmatrix_token_expires_at',
  TOKEN_TYPE: 'smartmatrix_token_type',
  ACTIVE_ACCOUNT_ID: 'smartmatrix_active_account_id'
} as const;

/**
 * Get account-specific storage keys
 */
function getAccountStorageKeys(accountId?: string | null): {
  ACCESS_TOKEN: string;
  REFRESH_TOKEN: string;
  TOKEN_EXPIRES_AT: string;
  TOKEN_TYPE: string;
  ACTIVE_ACCOUNT_ID: string;
} {
  if (!accountId) {
    // Use base keys for backward compatibility
    return {
      ACCESS_TOKEN: STORAGE_KEYS.ACCESS_TOKEN,
      REFRESH_TOKEN: STORAGE_KEYS.REFRESH_TOKEN,
      TOKEN_EXPIRES_AT: STORAGE_KEYS.TOKEN_EXPIRES_AT,
      TOKEN_TYPE: STORAGE_KEYS.TOKEN_TYPE,
      ACTIVE_ACCOUNT_ID: STORAGE_KEYS.ACTIVE_ACCOUNT_ID
    };
  }
  
  // Return account-specific keys
  return {
    ACCESS_TOKEN: `${STORAGE_KEYS.ACCESS_TOKEN}_${accountId}`,
    REFRESH_TOKEN: `${STORAGE_KEYS.REFRESH_TOKEN}_${accountId}`,
    TOKEN_EXPIRES_AT: `${STORAGE_KEYS.TOKEN_EXPIRES_AT}_${accountId}`,
    TOKEN_TYPE: `${STORAGE_KEYS.TOKEN_TYPE}_${accountId}`,
    ACTIVE_ACCOUNT_ID: STORAGE_KEYS.ACTIVE_ACCOUNT_ID
  };
}

/**
 * Token Manager Implementation
 * Handles secure storage and lifecycle management of JWT tokens
 */
export class TokenManager implements ITokenManager {
  private refreshTimer: NodeJS.Timeout | null = null;
  private refreshPromise: Promise<boolean> | null = null;

  /**
   * Get the current active account ID from storage
   */
  private getActiveAccountId(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return localStorage.getItem(STORAGE_KEYS.ACTIVE_ACCOUNT_ID);
  }

  /**
   * Set the active account ID in storage
   */
  private setActiveAccountId(accountId: string | null): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (accountId) {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_ACCOUNT_ID, accountId);
    } else {
      localStorage.removeItem(STORAGE_KEYS.ACTIVE_ACCOUNT_ID);
    }
  }

  /**
   * Set the account context for token operations (public method)
   * This should be called when switching accounts
   */
  setAccountContext(accountId: string | null): void {
    logger.auth('Setting account context', { accountId: accountId || 'default' });
    this.setActiveAccountId(accountId);
  }

  /**
   * Get the current account context (public method)
   */
  getAccountContext(): string | null {
    return this.getActiveAccountId();
  }

  /**
   * Store tokens securely in browser storage with verification
   * Now supports multi-account by using account-specific storage keys
   */
  storeTokens(tokens: TokenPair & { isLegacyToken?: boolean }, bypassIntegrityCheck: boolean = false): void {
    const startTime = Date.now();
    
    try {
      if (typeof window === 'undefined') {
        logger.warn('Cannot store tokens in server environment');
        return;
      }

      // Get the active account ID to determine which storage keys to use
      const activeAccountId = this.getActiveAccountId();
      const storageKeys = getAccountStorageKeys(activeAccountId);

      logger.auth('Storing tokens for account', { 
        activeAccountId: activeAccountId || 'default',
        storageKeys: Object.values(storageKeys)
      });

      // Store in localStorage for persistence across sessions
      localStorage.setItem(storageKeys.ACCESS_TOKEN, tokens.accessToken);
      localStorage.setItem(storageKeys.REFRESH_TOKEN, tokens.refreshToken);
      localStorage.setItem(storageKeys.TOKEN_EXPIRES_AT, tokens.expiresAt.toString());
      localStorage.setItem(storageKeys.TOKEN_TYPE, tokens.tokenType);

      // ⚠ A SCOPED write makes the un-scoped copies dead weight — drop them.
      //
      // Login stores tokens TWICE on purpose: once with a null account context (so the token is not
      // filed under the wrong account id while the account is still being resolved), then again once
      // the account id exists. The first write lands on the BASE keys and nothing used to remove it.
      //
      // That copy is not merely untidy. `getAccountStorageKeys(null)` falls back to exactly these
      // base keys, so any moment `activeAccountId` reads null sends the app to them — while
      // performTokenRefresh only ever rewrites the CURRENT (scoped) keys, so the base copy is frozen
      // at login time and goes stale, then expired. Reading it yields a 401 on a session that is
      // otherwise perfectly alive. It also leaves a full access+refresh pair sitting in an
      // account-agnostic slot across account switches.
      //
      // Only cleaned when we just wrote a scoped copy: a genuinely un-scoped (legacy) session still
      // needs its base keys, and that is the backward-compatibility path the fallback exists for.
      if (activeAccountId) {
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
        localStorage.removeItem(STORAGE_KEYS.TOKEN_TYPE);
      }

      // Mark as legacy token if needed
      if (tokens.isLegacyToken) {
        localStorage.setItem('smartmatrix_legacy_token_format', 'true');
        logger.auth('Stored legacy token with special handling');
      }

      const endTime = Date.now();
      logger.auth('Tokens stored successfully', { 
        duration: endTime - startTime,
        tokenLength: tokens.accessToken.length,
        expiresAt: new Date(tokens.expiresAt).toISOString(),
        timeUntilExpiry: Math.round((tokens.expiresAt - Date.now()) / 1000 / 60) + ' minutes',
        isLegacy: !!tokens.isLegacyToken,
        bypassCheck: bypassIntegrityCheck,
        accountId: activeAccountId || 'default'
      });
      
      // VERIFY storage immediately - critical for preventing race conditions
      // Skip integrity check during verification to avoid circular validation
      const accessToken = localStorage.getItem(storageKeys.ACCESS_TOKEN);
      const refreshToken = localStorage.getItem(storageKeys.REFRESH_TOKEN);
      
      // Verify access token was stored correctly
      const accessTokenStored = !!accessToken;
      const accessTokenMatches = accessToken === tokens.accessToken;
      const refreshTokenStored = refreshToken !== null; // Can be empty string for Google OAuth
      
      if (!accessTokenStored || !accessTokenMatches || !refreshTokenStored) {
        logger.error('Token storage verification FAILED', {
          stored: accessTokenStored,
          matches: accessTokenMatches,
          refreshTokenStored: refreshTokenStored,
          expectedLength: tokens.accessToken.length,
          actualLength: accessToken?.length || 0,
          accountId: activeAccountId || 'default',
          storageKeys: Object.keys(storageKeys)
        });
        throw new Error('Token storage verification failed');
      }
      
      logger.auth('Token storage verified successfully', {
        verificationTime: Date.now() - endTime,
        accountId: activeAccountId || 'default'
      });
      
      // Note: scheduleTokenRefresh() is NOT called here to avoid triggering getTokens()
      // which would run integrity validation. The caller should call scheduleTokenRefresh()
      // after successful login if needed.
    } catch (error) {
      logger.error('Failed to store tokens', error);
      throw new Error('Failed to store authentication tokens');
    }
  }

  /**
   * Retrieve tokens from storage
   * Now supports multi-account by using account-specific storage keys
   */
  getTokens(): TokenPair | null {
    try {
      if (typeof window === 'undefined') {
        return null;
      }

      // Get the active account ID to determine which storage keys to use
      const activeAccountId = this.getActiveAccountId();
      const storageKeys = getAccountStorageKeys(activeAccountId);

      const accessToken = localStorage.getItem(storageKeys.ACCESS_TOKEN);
      const refreshToken = localStorage.getItem(storageKeys.REFRESH_TOKEN);
      const expiresAtStr = localStorage.getItem(storageKeys.TOKEN_EXPIRES_AT);
      const tokenType = localStorage.getItem(storageKeys.TOKEN_TYPE);

      // Check for null (not stored) vs empty string (Google OAuth has empty refresh token initially)
      if (!accessToken || refreshToken === null || !expiresAtStr || !tokenType) {
        logger.auth('No tokens found for account', { accountId: activeAccountId || 'default' });
        return null;
      }

      const expiresAt = parseInt(expiresAtStr); // Parse as number timestamp
      
      // Validate expiration date
      if (isNaN(expiresAt)) {
        logger.warn('Invalid token expiration date, clearing tokens', { accountId: activeAccountId || 'default' });
        this.clearTokens();
        return null;
      }

      const tokens: TokenPair = {
        accessToken,
        refreshToken,
        expiresAt,
        tokenType: tokenType as 'Bearer'
      };

      logger.auth('Tokens retrieved for account', { 
        accountId: activeAccountId || 'default',
        tokenLength: accessToken.length,
        expiresAt: new Date(expiresAt).toISOString()
      });

      // Note: Token integrity validation removed to support various token formats
      // The backend is responsible for token validation
      // We only check expiration here
      
      return tokens;
    } catch (error) {
      logger.error('Failed to retrieve tokens', error);
      return null;
    }
  }

  /**
   * Clear all stored tokens
   * Now supports multi-account by clearing ALL account-specific storage keys
   * IMPORTANT: Preserves account storage data (smartmatrix_account_* and smartmatrix_accounts_list)
   */
  clearTokens(): void {
    try {
      if (typeof window === 'undefined') {
        return;
      }

      // Clear tokens for the current active account
      const activeAccountId = this.getActiveAccountId();
      const storageKeys = getAccountStorageKeys(activeAccountId);

      localStorage.removeItem(storageKeys.ACCESS_TOKEN);
      localStorage.removeItem(storageKeys.REFRESH_TOKEN);
      localStorage.removeItem(storageKeys.TOKEN_EXPIRES_AT);
      localStorage.removeItem(storageKeys.TOKEN_TYPE);
      
      // Clear ALL smartmatrix token keys (including other accounts)
      // BUT preserve account storage data for multi-account functionality
      const keysToRemove: string[] = [];
      const keysToPreserve = [
        'smartmatrix_account_',      // Account data (smartmatrix_account_*)
        'smartmatrix_accounts_list', // Accounts list
        'smartmatrix_active_account' // Active account ID (both with and without _id suffix)
      ];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('smartmatrix_')) {
          // Check if this key should be preserved
          const shouldPreserve = keysToPreserve.some(prefix => key.startsWith(prefix));
          if (!shouldPreserve) {
            keysToRemove.push(key);
          }
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      logger.auth('Cleared smartmatrix tokens from localStorage (preserved account data)', { 
        activeAccountId: activeAccountId || 'default',
        totalKeysCleared: keysToRemove.length
      });

      // Clear refresh timer
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }

      // Clear refresh promise
      this.refreshPromise = null;

      logger.auth('Tokens cleared successfully', { accountId: activeAccountId || 'default' });
    } catch (error) {
      logger.error('Failed to clear tokens', error);
    }
  }

  /**
   * Check if access token is valid (not expired)
   */
  isAccessTokenValid(): boolean {
    const tokens = this.getTokens();
    if (!tokens) {
      return false;
    }

    const now = Date.now();
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
    
    return tokens.expiresAt > (now + bufferTime);
  }

  /**
   * Check if refresh token is valid
   * Note: We can't validate refresh token expiration without server call
   * This method checks if refresh token exists and has basic structure
   */
  isRefreshTokenValid(): boolean {
    const tokens = this.getTokens();
    if (!tokens || !tokens.refreshToken) {
      return false;
    }

    // Basic validation - refresh token should be a non-empty string
    return tokens.refreshToken.length > 0;
  }

  /**
   * Get token expiration date
   */
  getTokenExpiration(): number | null {
    const tokens = this.getTokens();
    return tokens ? tokens.expiresAt : null;
  }

  /**
   * Refresh tokens if needed
   * Returns true if tokens are valid (either already valid or successfully refreshed)
   *
   * `force` skips the "already valid" short-circuit. Pass it from the 401 handler: the server has
   * just rejected this token, so what the local clock thinks is irrelevant. Without it, a 401 on a
   * not-yet-expired token short-circuited to `true`, the caller replayed the SAME dead token, and
   * after MAX_RETRY_ATTEMPTS the operator was logged out — a session kill with no refresh ever
   * attempted.
   */
  async refreshTokensIfNeeded(force = false): Promise<boolean> {
    try {
      // If already refreshing, wait for that operation. Checked BEFORE `force` so a forced caller
      // joins an in-flight refresh rather than starting a second one — the backend ROTATES the
      // refresh token, so two overlapping refreshes would leave the loser holding a spent token.
      if (this.refreshPromise) {
        return await this.refreshPromise;
      }

      // Check if access token is still valid
      if (!force && this.isAccessTokenValid()) {
        return true;
      }

      // Check if refresh token is available
      if (!this.isRefreshTokenValid()) {
        logger.auth('No valid refresh token available');
        return false;
      }

      // Start refresh operation
      this.refreshPromise = this.performTokenRefresh();
      const result = await this.refreshPromise;
      this.refreshPromise = null;

      return result;
    } catch (error) {
      this.refreshPromise = null;
      logger.error('Token refresh failed', error);
      return false;
    }
  }

  /**
   * Perform the actual token refresh
   */
  private async performTokenRefresh(): Promise<boolean> {
    try {
      const tokens = this.getTokens();
      if (!tokens || !tokens.refreshToken) {
        return false;
      }

      logger.auth('Attempting to refresh tokens');

      // Import API client and constants dynamically to avoid circular dependencies
      const { apiClient } = await import('../../utils/apiClient');
      const { API_TOKENS_RENEW } = await import('../../constants/apiConstants');
      const { PARTITION_KEY } = await import('../../constants/coreConstants');

      // ⚠ `partitionKey` is REQUIRED — omitting it made this endpoint 400 before the request ever
      // reached the handler, so token refresh NEVER worked: not the 10-minutes-before-expiry timer,
      // not the pre-flight refresh, not the 401 retry. Every attempt threw, the catch below cleared
      // the tokens, and the operator was bounced to /login roughly every 55 minutes.
      //
      // `SysLogin_RenewToken_RequestValidator` enforces `PartitionKey.NotEmpty()`, and FluentValidation
      // runs BEFORE the action — so `SysTokenController.RenewToken`'s own "default the partition key"
      // branch is unreachable and cannot save us. Six sibling auth validators require it too and
      // `loginService` supplies it on all three login paths; refresh was simply the caller that forgot.
      const response = await apiClient.post(API_TOKENS_RENEW, {
        partitionKey: PARTITION_KEY,
        refreshToken: tokens.refreshToken
      });

      // Extract token data from response
      const tokenData = response.data?.data?.token;
      if (!tokenData) {
        throw new Error('Invalid token refresh response');
      }

      // Create new token pair
      const newTokens: TokenPair = {
        accessToken: tokenData.accessToken,
        refreshToken: tokenData.refreshToken,
        expiresAt: Date.now() + (tokenData.accessToken_LifeInMinutes * 60 * 1000),
        tokenType: 'Bearer'
      };

      // Store new tokens
      this.storeTokens(newTokens);

      logger.auth('Tokens refreshed successfully');
      return true;
    } catch (error) {
      logger.error('Token refresh operation failed', error);
      
      // Clear invalid tokens
      this.clearTokens();
      return false;
    }
  }

  /**
   * Schedule automatic token refresh
   */
  scheduleTokenRefresh(): void {
    try {
      // Clear existing timer
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
      }

      const tokens = this.getTokens();
      if (!tokens) {
        return;
      }

      const now = Date.now();
      const expiresAt = tokens.expiresAt;
      const refreshTime = expiresAt - (10 * 60 * 1000); // Refresh 10 minutes before expiry

      if (refreshTime <= now) {
        // Token expires soon, refresh immediately
        this.refreshTokensIfNeeded();
        return;
      }

      const timeUntilRefresh = refreshTime - now;
      
      this.refreshTimer = setTimeout(() => {
        this.refreshTokensIfNeeded();
      }, timeUntilRefresh);

      logger.auth(`Token refresh scheduled in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`);
    } catch (error) {
      logger.error('Failed to schedule token refresh', error);
    }
  }

  /**
   * Enhanced token validation that handles both JWT and legacy tokens
   */
  validateTokenIntegrity(token: string): boolean {
    try {
      if (!token || typeof token !== 'string') {
        return false;
      }

      // Check if this is a legacy token by looking for the legacy flag
      const isLegacyToken = localStorage.getItem('smartmatrix_legacy_token_format') === 'true';
      
      if (isLegacyToken) {
        // Legacy token validation - more permissive
        return this.validateLegacyToken(token);
      }

      // Check if it's a JWT token (has 3 parts separated by dots)
      const parts = token.split('.');
      if (parts.length === 3) {
        return this.validateJWTToken(token, parts);
      } else {
        // Non-JWT token - treat as legacy
        return this.validateLegacyToken(token);
      }
    } catch (error) {
      logger.error('Token integrity validation failed', error);
      return false;
    }
  }

  /**
   * Validate JWT tokens
   */
  private validateJWTToken(_token: string, parts: string[]): boolean {
    try {
      // Each part should be base64 encoded
      for (const part of parts) {
        if (!part || part.length === 0) {
          return false;
        }
      }

      // Try to decode the payload (middle part)
      const payload = JSON.parse(atob(parts[1]));
      
      // Check for required JWT claims
      if (!payload.exp || !payload.iat) {
        return false;
      }

      // Check if token is not expired (basic check)
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp <= now) {
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Validate legacy (non-JWT) tokens
   */
  private validateLegacyToken(token: string): boolean {
    try {
      // Basic validation for legacy tokens
      if (token.length < 10 || token.length > 2000) {
        return false;
      }
      
      // Check if it contains only valid characters
      const validTokenPattern = /^[A-Za-z0-9+/=._-]+$/;
      if (!validTokenPattern.test(token)) {
        return false;
      }
      
      // For legacy tokens, we rely on expiration time stored separately
      const expiresAtStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
      if (expiresAtStr) {
        const expiresAt = parseInt(expiresAtStr);
        const now = Date.now();
        const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
        
        if (expiresAt <= (now + bufferTime)) {
          return false; // Token expired
        }
      }
      
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Clear migration flag (called after successful migration)
   */
  clearMigrationFlag(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('smartmatrix_migrated_token_flag');
      logger.auth('Migration flag cleared manually');
    }
  }

  /**
   * Get current token status
   */
  getTokenStatus(): TokenStatus {
    const tokens = this.getTokens();
    
    if (!tokens) {
      return TokenStatus.Invalid;
    }

    if (this.refreshPromise) {
      return TokenStatus.Refreshing;
    }

    if (this.isAccessTokenValid()) {
      return TokenStatus.Valid;
    }

    if (this.isRefreshTokenValid()) {
      return TokenStatus.Expired;
    }

    return TokenStatus.Invalid;
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.refreshPromise = null;
  }
}

// Export singleton instance
export const tokenManager = new TokenManager();